import React, { useEffect, useMemo, useRef, useState } from "react"
import Chart from "react-apexcharts"
import moment from "moment"
import { FaDotCircle, FaInfoCircle } from "react-icons/fa"
import secureLocalStorage from "react-secure-storage"
import {
  getSameWeekNextYear,
  getSameWeekPreviousYear,
  getWeekTooltipLabel,
} from "../../utils/dateUtil"

const SuccessRateChart = ({
  data,
  previousData,
  maxValue = 280000,
  showWeekCard = false,
  sortOrder: sortOrderProp,
  onSortOrderChange,
}) => {
  const [showFormula, setShowFormula] = useState(false)
  const [prevWeekInput, setPrevWeekInput] = useState("")
  const [currentYtdInput, setCurrentYtdInput] = useState("")
  // ── NEW: Week-to-Week Year-on-Year comparison (third box) ──
  const [wtwCurrentWeek, setWtwCurrentWeek] = useState("")
  const [wtwPrevWeek, setWtwPrevWeek] = useState("")
  const [wtwCurrentInput, setWtwCurrentInput] = useState("")
  const [wtwPrevInput, setWtwPrevInput] = useState("")
  const [wtwWeekErrorCurrent, setWtwWeekErrorCurrent] = useState("")
  const [wtwWeekErrorPrev, setWtwWeekErrorPrev] = useState("")
  // If sortOrder is controlled from parent (HistoryPage), use that; else fall back to local state
  const [localSortOrder, setLocalSortOrder] = useState("chronological")
  const sortOrder = sortOrderProp !== undefined ? sortOrderProp : localSortOrder
  const setSortOrder = onSortOrderChange ?? setLocalSortOrder

  const salesRevenue = data || []

  const currentYear = salesRevenue?.length
    ? moment(salesRevenue[0].date).year()
    : moment().year()

  const previousYear = previousData?.length
    ? moment(previousData[0].date).year()
    : currentYear - 1

  // ✅ Union of BOTH years' actual ISO weeks — sorted chronologically
  const allWeeks = useMemo(() => {
    const weekSet = new Set()
    salesRevenue.forEach(item => weekSet.add(moment(item.date).isoWeek()))
    previousData?.forEach(item => weekSet.add(moment(item.date).isoWeek()))
    return Array.from(weekSet).sort((a, b) => a - b)
  }, [salesRevenue, previousData])

  // ✅ Current year: ISO week → amount
  const currentByWeek = useMemo(() => {
    const map = {}
    salesRevenue.forEach(item => {
      map[moment(item.date).isoWeek()] = Number(item.sumWeeklySalesValue) || 0
    })
    return map
  }, [salesRevenue])

  // ✅ Previous year: ISO week → amount
  const prevByWeek = useMemo(() => {
    const map = {}
    previousData?.forEach(item => {
      map[moment(item.date).isoWeek()] = Number(item.sumWeeklySalesValue) || 0
    })
    return map
  }, [previousData])

  // ✅ Date lookups by ISO week (current year)
  const dateByWeek = useMemo(() => {
    const map = {}
    salesRevenue.forEach(item => {
      map[moment(item.date).isoWeek()] = moment(item.date).format("DD-MM-YYYY")
    })
    return map
  }, [salesRevenue])

  // ✅ Date lookups by ISO week (previous year)
  const prevDateByWeek = useMemo(() => {
    const map = {}
    previousData?.forEach(item => {
      map[moment(item.date).isoWeek()] = moment(item.date).format("DD-MM-YYYY")
    })
    return map
  }, [previousData])

  // ── WTW: Independent weeks (Current Week / Previous Week can differ) ──
  // Year is automatic: uses currentYear (from data) and previousYear = currentYear -1
  // Gracefully handles missing Week 53 (empty, not duplicate)
  useEffect(() => {
    if (!wtwCurrentWeek) {
      setWtwCurrentInput("")
      setWtwWeekErrorCurrent("")
      return
    }
    const weekNum = Number(wtwCurrentWeek)
    if (!Number.isInteger(weekNum) || weekNum < 1 || weekNum > 53) {
      setWtwWeekErrorCurrent("Enter 1–53")
      return
    }
    setWtwWeekErrorCurrent("")
    const hasCurrent = Object.prototype.hasOwnProperty.call(
      currentByWeek,
      weekNum
    )
    const autoCurrent = hasCurrent ? currentByWeek[weekNum] : undefined
    setWtwCurrentInput(autoCurrent !== undefined ? String(autoCurrent) : "")
  }, [wtwCurrentWeek, currentByWeek])

  useEffect(() => {
    if (!wtwPrevWeek) {
      setWtwPrevInput("")
      setWtwWeekErrorPrev("")
      return
    }
    const weekNum = Number(wtwPrevWeek)
    if (!Number.isInteger(weekNum) || weekNum < 1 || weekNum > 53) {
      setWtwWeekErrorPrev("Enter 1–53")
      return
    }
    setWtwWeekErrorPrev("")
    const hasPrev = Object.prototype.hasOwnProperty.call(prevByWeek, weekNum)
    const autoPrev = hasPrev ? prevByWeek[weekNum] : undefined
    setWtwPrevInput(autoPrev !== undefined ? String(autoPrev) : "")
  }, [wtwPrevWeek, prevByWeek])

  // ── WTW: Auto-default BOTH week inputs to latest imported week on Reports open ──
  // When Reports opens, automatically select latest isoWeek from salesRevenue for BOTH rows.
  // Example: latest = W45 2026 → both rows default to 45 (editable to different weeks). If later W46 imported → both advance if still at old latest.
  // NOTE: depends only on salesRevenue/showWeekCard (not on wtwCurrentWeek/wtwPrevWeek) and
  // reads the live values via refs — otherwise every keystroke re-triggers this effect and its
  // "refill when empty" branch snaps the field back before the user can type a replacement,
  // making it impossible to backspace a week number and enter a new one manually.
  const prevLatestWtwWeekRef = useRef(null)
  const wtwCurrentWeekRef = useRef(wtwCurrentWeek)
  const wtwPrevWeekRef = useRef(wtwPrevWeek)
  useEffect(() => {
    wtwCurrentWeekRef.current = wtwCurrentWeek
  }, [wtwCurrentWeek])
  useEffect(() => {
    wtwPrevWeekRef.current = wtwPrevWeek
  }, [wtwPrevWeek])

  useEffect(() => {
    if (!showWeekCard) return
    if (!salesRevenue || salesRevenue.length === 0) return
    let latestMoment = null
    for (const item of salesRevenue) {
      if (!item?.date) continue
      const m = moment(item.date)
      if (!m.isValid()) continue
      if (!latestMoment || m.isAfter(latestMoment)) latestMoment = m
    }
    if (!latestMoment) return
    const latestWeekStr = String(latestMoment.isoWeek())
    const prevLatest = prevLatestWtwWeekRef.current
    prevLatestWtwWeekRef.current = latestWeekStr

    const curCurrentWeek = wtwCurrentWeekRef.current
    const curPrevWeek = wtwPrevWeekRef.current

    // Initial open: both empty → set both to latest
    if (!curCurrentWeek && !curPrevWeek && prevLatest === null) {
      setWtwCurrentWeek(latestWeekStr)
      setWtwPrevWeek(latestWeekStr)
      return
    }
    // Auto-advance: only a row still sitting on the OLD latest (i.e. untouched by the
    // user) moves forward when a newer week is imported — manual edits are left alone.
    if (curCurrentWeek === prevLatest && latestWeekStr !== prevLatest) {
      setWtwCurrentWeek(latestWeekStr)
    }
    if (curPrevWeek === prevLatest && latestWeekStr !== prevLatest) {
      setWtwPrevWeek(latestWeekStr)
    }
  }, [salesRevenue, showWeekCard])

  // Editing/clearing the Current Year week mirrors into Previous Year's week too
  // (Previous Year stays independently editable afterward if the user changes it directly).
  const handleWtwCurrentWeekChange = e => {
    const val = e.target.value
    if (val === "") {
      setWtwCurrentWeek("")
      setWtwPrevWeek("")
      return
    }
    if (!/^\d*$/.test(val)) return
    setWtwCurrentWeek(val)
    setWtwPrevWeek(val)
  }
  const handleWtwPrevWeekChange = e => {
    const val = e.target.value
    if (val === "") {
      setWtwPrevWeek("")
      return
    }
    if (!/^\d*$/.test(val)) return
    setWtwPrevWeek(val)
  }

  const handleWtwCurrentChange = e => setWtwCurrentInput(e.target.value)
  const handleWtwPrevChange = e => setWtwPrevInput(e.target.value)

  // ✅ Apply Sort Order to weeks.
  // Sorting key = max revenue across BOTH years for that week so both lines move together.
  // - "descending": position 0 = highest revenue week, last = lowest
  // - "ascending":  position 0 = lowest revenue week, last = highest
  // - "chronological": Week 1 → Week 52 (default)
  const displayWeeks = useMemo(() => {
    if (sortOrder === "ascending") {
      return [...allWeeks].sort((a, b) => {
        const valA = Math.max(currentByWeek[a] ?? 0, prevByWeek[a] ?? 0)
        const valB = Math.max(currentByWeek[b] ?? 0, prevByWeek[b] ?? 0)
        return valA !== valB ? valA - valB : a - b
      })
    }
    if (sortOrder === "descending") {
      return [...allWeeks].sort((a, b) => {
        const valA = Math.max(currentByWeek[a] ?? 0, prevByWeek[a] ?? 0)
        const valB = Math.max(currentByWeek[b] ?? 0, prevByWeek[b] ?? 0)
        return valA !== valB ? valB - valA : a - b
      })
    }
    return allWeeks
  }, [allWeeks, currentByWeek, prevByWeek, sortOrder])

  // ✅ Build series arrays — both series use the same displayWeeks order
  const currentValues = displayWeeks.map(w => currentByWeek[w] ?? null)
  const prevValues = displayWeeks.map(w => prevByWeek[w] ?? null)
  // X-axis labels: show the ISO week number for each position
  const categories = displayWeeks.map(w => `${w}`)

  const series = [
    { name: `Current Year (${currentYear})`, data: currentValues },
    { name: `Previous Year (${previousYear})`, data: prevValues },
  ]

  const options = {
    chart: {
      type: "area",
      height: 350,
      toolbar: { show: false },
      background: "transparent",
      foreColor: "#1a1a2e",
      animations: { enabled: true, speed: 400 },
    },
    dataLabels: { enabled: false },
    stroke: {
      show: true,
      curve: "smooth",
      width: [2.5, 2.5],
      dashArray: [0, 6],
    },
    markers: {
      size: [4, 3],
      colors: ["#fff", "#fff"],
      strokeColors: ["#4361ee", "#4CBB17"],
      strokeWidth: 2,
      hover: { size: 6 },
    },
    fill: {
      type: "gradient",
      gradient: {
        shadeIntensity: 1,
        opacityFrom: 0.35,
        opacityTo: 0.02,
        stops: [0, 50, 100],
        colorStops: [
          { offset: 0, color: "#4361ee", opacity: 0.35 },
          { offset: 100, color: "#ddd", opacity: 0.02 },
        ],
      },
      opacity: [0.35, 0],
    },
    colors: ["#4361ee", "#4CBB17"],
    xaxis: {
      // categories drives actual data alignment; overwriteCategories shows week numbers as labels
      categories,
      overwriteCategories: categories,
      axisBorder: { show: false },
      axisTicks: { show: false },
      tooltip: { enabled: false },
      labels: {
        rotate: -45,
        style: { colors: "#1a1a2e", fontSize: "11px", fontWeight: 600 },
      },
    },
    yaxis: {
      title: {
        text: "Revenue (£)",
        style: { fill: "#111", fontSize: "12px", fontWeight: 700 },
      },
      labels: {
        style: { fontSize: "12px", fontWeight: 600 },
        formatter: val => `£ ${val?.toLocaleString("en-GB")}`,
      },
    },
    grid: {
      borderColor: "#e8e8f0",
      strokeDashArray: 4,
      xaxis: { lines: { show: false } },
      yaxis: { lines: { show: true } },
    },
    tooltip: {
      shared: true,
      intersect: false,
      theme: "light",
      style: { fontSize: "13px" },
      x: {
        formatter: (val, { dataPointIndex }) => {
          const weekNum = displayWeeks[dataPointIndex]

          const currentDate = dateByWeek[weekNum]
          const prevDate = prevDateByWeek[weekNum]

          const resolvedCurrentDate =
            currentDate || (prevDate ? getSameWeekNextYear(prevDate) : null)
          const resolvedPrevDate =
            prevDate ||
            (currentDate ? getSameWeekPreviousYear(currentDate) : null)

          const currentLabel = getWeekTooltipLabel(
            weekNum - 1,
            resolvedCurrentDate
          )
          const prevLabel = getWeekTooltipLabel(weekNum - 1, resolvedPrevDate)

          return `Current Year : ${
            currentLabel || "N/A"
          }<br/> Previous Year : ${prevLabel || "N/A"}`
        },
      },
      y: {
        formatter: val =>
          val !== null && val !== undefined
            ? `£ ${val.toLocaleString("en-GB")}`
            : "N/A",
      },
    },
    legend: { show: false },
    responsive: [
      {
        breakpoint: 600,
        options: {
          xaxis: {
            labels: {
              rotate: -45,
              rotateAlways: true,
              style: { fontSize: "8px", fontWeight: 600 },
            },
          },
          yaxis: {
            labels: {
              style: { fontSize: "9px", fontWeight: 600 },
              formatter: val => `£${Math.round(val / 1000)}K`,
            },
          },
        },
      },
      {
        breakpoint: 900,
        options: {
          xaxis: {
            labels: {
              rotate: -45,
              rotateAlways: allWeeks.length > 15,
              style: { fontSize: "9px", fontWeight: 600 },
            },
          },
          yaxis: {
            labels: {
              style: { fontSize: "10px", fontWeight: 600 },
              formatter: val => `£${Math.round(val / 1000)}K`,
            },
          },
        },
      },
    ],
  }

  const currentWeekSet = new Set(
    salesRevenue.map(i => moment(i.date).isoWeek())
  )
  const matchedPrevious = (previousData || []).filter(i =>
    currentWeekSet.has(moment(i.date).isoWeek())
  )

  const currentTotal = salesRevenue.reduce(
    (s, i) => s + Number(i.sumWeeklySalesValue),
    0
  )
  const previousTotal = matchedPrevious.reduce(
    (s, i) => s + Number(i.sumWeeklySalesValue),
    0
  )
  const diff = currentTotal - previousTotal

  const currentAvg = salesRevenue.length
    ? currentTotal / salesRevenue.length
    : 0
  const previousAvg = matchedPrevious.length
    ? previousTotal / matchedPrevious.length
    : 0
  const diffPct = (((currentAvg - previousAvg) / maxValue) * 100).toFixed(1)

  const fmtGBP = v =>
    "£ " + Math.abs(v).toLocaleString("en-GB", { maximumFractionDigits: 0 })

  // ── Financial YTD: system-calculated Current Year (trailing YTD) + manual Previous Year ──
  // Current Year auto = SUM of revenue for period Week X previous year → Week X current year
  // where X = latest ISO week from salesRevenue (dynamic, not hard-coded 45). Reuses existing
  // weekly revenue data (salesRevenue + previousData) and moment isoWeek/year logic.
  const financialYtdCurrentAuto = useMemo(() => {
    if (!salesRevenue || salesRevenue.length === 0) return null
    let latestMoment = null
    for (const item of salesRevenue) {
      if (!item?.date) continue
      const m = moment(item.date)
      if (!m.isValid()) continue
      if (!latestMoment || m.isAfter(latestMoment)) latestMoment = m
    }
    if (!latestMoment) {
      // No valid dates — fallback to simple sum of current data (handles missing week/data gracefully)
      return salesRevenue.reduce((s, i) => {
        const v = Number(i.sumWeeklySalesValue)
        return s + (Number.isFinite(v) ? v : 0)
      }, 0)
    }
    const latestWeek = latestMoment.isoWeek()
    const latestYear =
      typeof latestMoment.isoWeekYear === "function"
        ? latestMoment.isoWeekYear()
        : latestMoment.year()
    const startWeek = latestWeek
    const startYear = latestYear - 1
    const combined = [...salesRevenue, ...(previousData || [])]
    let sum = 0
    let hasData = false
    for (const item of combined) {
      if (!item?.date) continue
      const m = moment(item.date)
      if (!m.isValid()) continue
      const y = typeof m.isoWeekYear === "function" ? m.isoWeekYear() : m.year()
      const w = m.isoWeek()
      const inPeriod =
        (y === startYear && w >= startWeek) ||
        (y === latestYear && w <= latestWeek) ||
        (y > startYear && y < latestYear)
      if (inPeriod) {
        const v = Number(item.sumWeeklySalesValue)
        sum += Number.isFinite(v) ? v : 0
        hasData = true
      }
    }
    if (!hasData) {
      return salesRevenue.reduce((s, i) => {
        const v = Number(i.sumWeeklySalesValue)
        return s + (Number.isFinite(v) ? v : 0)
      }, 0)
    }
    return sum
  }, [salesRevenue, previousData])

  // ── Prev YTD auto-calc gate ──
  // Martin: Prev YTD should only auto-calculate once the CURRENT year's imported
  // data reaches December — even just one week of December data is enough to
  // trigger it, it doesn't need to be the year's very last week — matching how
  // much of the previous year we already have, so the YoY comparison is fair.
  // Before that point (e.g. current year data only reaching August), Prev YTD
  // stays manual, same as today.
  const hasDecemberDataCurrentYear = useMemo(() => {
    if (!salesRevenue || salesRevenue.length === 0) return false
    return salesRevenue.some(item => {
      if (!item?.date) return false
      const m = moment(item.date)
      return m.isValid() && m.month() === 11 // December is month index 11
    })
  }, [salesRevenue])

  // Once the current year's data reaches December, Prev YTD auto-calculates as
  // the previous year's full-year total (mirrors how Current YTD's trailing
  // window collapses to a plain full-year sum once the current year is complete).
  const prevYtdAuto = useMemo(() => {
    if (!hasDecemberDataCurrentYear) return null
    if (!previousData || previousData.length === 0) return null
    return previousData.reduce((s, i) => {
      const v = Number(i.sumWeeklySalesValue)
      return s + (Number.isFinite(v) ? v : 0)
    }, 0)
  }, [hasDecemberDataCurrentYear, previousData])

  const weekCurrent =
    currentYtdInput !== "" ? parseFloat(currentYtdInput) || 0 : null
  const weekPrev = prevWeekInput !== "" ? parseFloat(prevWeekInput) || 0 : null
  const weekDiff =
    weekCurrent !== null && weekPrev !== null ? weekCurrent - weekPrev : null
  // % difference for Financial YTD — YoY: (Difference ÷ Prev YTD) × 100
  // This makes the % visibly change when you type. Previous Target-based formula
  // gave 0.0% for small YTD values (e.g. 500/280000 → 0.2%) which looked broken.
  const weekDiffPct = (() => {
    if (weekDiff === null || weekPrev === null) return null
    if (weekPrev !== 0)
      return ((weekDiff / Math.abs(weekPrev)) * 100).toFixed(1)
    if (weekCurrent === 0) return "0.0" // 0 vs 0
    return "100.0" // prev 0 → current >0 : treat as 100% growth (avoid 0% fallback)
  })()

  // ── WTW: Difference £ and % (READ-ONLY, auto from wtwCurrentInput/wtwPrevInput) ──
  const wtwCurrentNum =
    wtwCurrentInput !== "" ? parseFloat(wtwCurrentInput) : null
  const wtwPrevNum = wtwPrevInput !== "" ? parseFloat(wtwPrevInput) : null
  const hasWtwCurrent = wtwCurrentNum !== null && !isNaN(wtwCurrentNum)
  const hasWtwPrev = wtwPrevNum !== null && !isNaN(wtwPrevNum)
  const wtwDiff =
    hasWtwCurrent && hasWtwPrev ? wtwCurrentNum - wtwPrevNum : null
  const wtwDiffPct = (() => {
    if (wtwDiff === null || !hasWtwPrev) return null
    if (wtwPrevNum !== 0) return (wtwDiff / Math.abs(wtwPrevNum)) * 100
    if (wtwDiff === 0) return 0
    return 100
  })()
  const wtwDiffPctDisplay =
    wtwDiffPct !== null ? Number(wtwDiffPct).toFixed(1) : null
  // Year for WTW is automatic: Previous = Current -1 (reuse same currentYear/previousYear, but enforce -1)
  const wtwPreviousYear = currentYear - 1

  // ── Financial YTD Comparison persistence ──
  // Both Current YTD and Prev YTD are editable and persisted via secureLocalStorage
  // so they survive leaving/reopening Reports. Current YTD always defaults to the
  // system-calculated value until the user overrides it. Prev YTD defaults to the
  // auto-calculated value only once prevYtdAuto is available (current year data
  // reaches a full year) — until then it stays manual/empty, same as before.
  const getFinancialYtdKeys = (cYear, pYear) => ({
    current: `financialYtd_current_${cYear}`,
    prev: `financialYtd_prev_${pYear}`,
  })

  // Load saved Previous Year and Current Year on mount / when year changes.
  // Current Year falls back to the auto-calculated value when nothing is saved.
  useEffect(() => {
    const keys = getFinancialYtdKeys(currentYear, previousYear)
    const savedPrev = secureLocalStorage.getItem(keys.prev)
    if (savedPrev !== null && savedPrev !== undefined && savedPrev !== "") {
      setPrevWeekInput(String(savedPrev))
    } else if (prevYtdAuto !== null && prevYtdAuto !== undefined) {
      setPrevWeekInput(String(prevYtdAuto))
    } else {
      setPrevWeekInput("")
    }
    const savedCurrent = secureLocalStorage.getItem(keys.current)
    if (
      savedCurrent !== null &&
      savedCurrent !== undefined &&
      savedCurrent !== ""
    ) {
      setCurrentYtdInput(String(savedCurrent))
    } else if (
      financialYtdCurrentAuto !== null &&
      financialYtdCurrentAuto !== undefined
    ) {
      setCurrentYtdInput(String(financialYtdCurrentAuto))
    } else {
      setCurrentYtdInput("")
    }
  }, [currentYear, previousYear, financialYtdCurrentAuto, prevYtdAuto])

  const handleFinancialPrevChange = e => {
    const val = e.target.value
    setPrevWeekInput(val)
    const keys = getFinancialYtdKeys(currentYear, previousYear)
    if (val === "" || val === null) {
      secureLocalStorage.removeItem(keys.prev)
    } else {
      secureLocalStorage.setItem(keys.prev, val)
    }
  }

  const handleFinancialCurrentChange = e => {
    const val = e.target.value
    setCurrentYtdInput(val)
    const keys = getFinancialYtdKeys(currentYear, previousYear)
    if (val === "" || val === null) {
      secureLocalStorage.removeItem(keys.current)
    } else {
      secureLocalStorage.setItem(keys.current, val)
    }
  }

  return (
    <div>
      {/* ── Header: title + Sort (Sort pinned to the right corner) —
          typography matches the History Charts page header ── */}
      <div className="d-flex justify-content-between align-items-center mb-3 px-2 flex-wrap gap-3">
        <div>
          <h5
            style={{
              margin: 0,
              fontSize: "18px",
              fontWeight: 700,
              color: "#0f172a",
              letterSpacing: "-0.3px",
            }}
          >
            Sales Revenue History
          </h5>
        </div>
        {showWeekCard &&
          (() => {
            const STATES = [
              {
                key: "chronological",
                icon: "⇅",
                label: "Sort",
                color: "#64748b",
                bg: "#f1f5f9",
                border: "#e2e8f0",
              },
              {
                key: "ascending",
                icon: "↑",
                label: "Low → High",
                color: "#2563eb",
                bg: "#eff6ff",
                border: "#bfdbfe",
              },
              {
                key: "descending",
                icon: "↓",
                label: "High → Low",
                color: "#0f766e",
                bg: "#f0fdfa",
                border: "#99f6e4",
              },
            ]
            const current = STATES.find(s => s.key === sortOrder) || STATES[0]
            const nextKey =
              sortOrder === "chronological"
                ? "ascending"
                : sortOrder === "ascending"
                ? "descending"
                : "chronological"
            return (
              <button
                onClick={() => setSortOrder(nextKey)}
                title={`Sort Order: ${current.label} — click to change`}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                  background: current.bg,
                  border: `1.5px solid ${current.border}`,
                  borderRadius: "12px",
                  padding: "9px 16px",
                  cursor: "pointer",
                  outline: "none",
                  whiteSpace: "nowrap",
                  userSelect: "none",
                  minWidth: "130px",
                }}
              >
                <span
                  style={{
                    fontSize: "18px",
                    lineHeight: 1,
                    color: current.color,
                    fontWeight: 700,
                    minWidth: "20px",
                    textAlign: "center",
                  }}
                >
                  {current.icon}
                </span>
                <span
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "flex-start",
                  }}
                >
                  <span
                    style={{
                      fontSize: "9px",
                      fontWeight: 700,
                      color: "#94a3b8",
                      textTransform: "uppercase",
                      letterSpacing: "0.5px",
                      lineHeight: 1.2,
                    }}
                  >
                    Sort Order
                  </span>
                  <span
                    style={{
                      fontSize: "12px",
                      fontWeight: 700,
                      color: current.color,
                      lineHeight: 1.4,
                    }}
                  >
                    {current.label}
                  </span>
                </span>
              </button>
            )
          })()}
      </div>

      {/* ── 3 comparison boxes side-by-side, equal width ──
          Uses CSS grid with equal fr columns so Financial YTD, Stats grid and the
          Week-to-Week box stay evenly sized in one row regardless of how much
          width the sidebar menu (open/collapsed) leaves available, and wrap
          responsively on tablet/mobile. Existing boxes are NOT modified. */}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: showWeekCard
            ? "minmax(0, 1fr) minmax(0, 1.2fr) minmax(0, 1.3fr)"
            : "minmax(0, 1fr)",
          gap: "8px",
          marginBottom: "16px",
          alignItems: "stretch",
        }}
      >
        {/* ── Box 1: Financial YTD Comparison — compact, reduced width/space ── */}
        {showWeekCard && (
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "1fr",
              gap: "4px",
              background: "#f8fafc",
              border: "1px solid #e2e8f0",
              borderRadius: "12px",
              padding: "8px 12px",
              minWidth: "0",
              width: "100%",
              boxSizing: "border-box",
              minHeight: "120px",
            }}
          >
            <div
              style={{
                fontSize: "10px",
                fontWeight: 700,
                color: "#64748b",
                textTransform: "uppercase",
                letterSpacing: "0.6px",
                marginBottom: "2px",
              }}
            >
              Financial YTD Comparison
            </div>
            {/* Current Week input — AUTO system-calculated, readOnly (Requirement: user must NOT manually enter) */}
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: "6px",
                minWidth: 0,
              }}
            >
              <FaDotCircle
                style={{ color: "#4361ee", fontSize: "10px", flexShrink: 0 }}
              />
              <span
                style={{ fontSize: "12px", color: "#374151", flexShrink: 0 }}
              >
                Current YTD
              </span>
              <input
                type="number"
                value={currentYtdInput}
                onChange={handleFinancialCurrentChange}
                placeholder="Auto-calculated"
                title="Editable — defaults to the value auto-calculated from weekly revenue"
                style={{
                  flex: "1 1 auto",
                  minWidth: "60px",
                  maxWidth: "100px",
                  boxSizing: "border-box",
                  fontSize: "12px",
                  border: "1px solid #cbd5e1",
                  borderRadius: "6px",
                  padding: "2px 4px",
                  outline: "none",
                  background: "#fff",
                  color: "#334155",
                  marginLeft: "auto",
                }}
              />
            </div>
            {/* Previous Week input */}
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: "6px",
                minWidth: 0,
                borderTop: "1px dashed #e2e8f0",
                paddingTop: "4px",
              }}
            >
              <FaDotCircle
                style={{ color: "#4CBB17", fontSize: "10px", flexShrink: 0 }}
              />
              <span
                style={{ fontSize: "12px", color: "#374151", flexShrink: 0 }}
              >
                Prev YTD
              </span>
              <input
                type="number"
                value={prevWeekInput}
                onChange={handleFinancialPrevChange}
                placeholder={
                  prevYtdAuto !== null ? "Auto-calculated" : "Enter value"
                }
                title={
                  prevYtdAuto !== null
                    ? "Editable — defaults to the previous year's full-year total now that the current year's data is complete"
                    : undefined
                }
                style={{
                  flex: "1 1 auto",
                  minWidth: "60px",
                  maxWidth: "100px",
                  boxSizing: "border-box",
                  fontSize: "12px",
                  border: "1px solid #cbd5e1",
                  borderRadius: "6px",
                  padding: "2px 6px",
                  outline: "none",
                  background: "#fff",
                  marginLeft: "auto",
                }}
              />
            </div>
            {/* Difference row — now with % difference (same formula as other card) */}
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: "6px",
                borderTop: "1px dashed #e2e8f0",
                paddingTop: "4px",
              }}
            >
              <span
                style={{ fontSize: "12px", color: "#374151", flexShrink: 0 }}
              >
                Difference
              </span>
              {weekDiff !== null ? (
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    flexWrap: "wrap",
                    justifyContent: "flex-end",
                    gap: "4px 6px",
                    marginLeft: "auto",
                    minWidth: 0,
                  }}
                >
                  <span
                    style={{
                      fontSize: "13px",
                      fontWeight: 700,
                      color: weekDiff >= 0 ? "#15803d" : "#be123c",
                    }}
                  >
                    {weekDiff >= 0 ? "+" : "-"}
                    {fmtGBP(weekDiff)}
                  </span>
                  {weekDiffPct !== null && (
                    <span
                      style={{
                        fontSize: "13px",
                        fontWeight: 700,
                        color: weekDiff >= 0 ? "#15803d" : "#be123c",
                      }}
                    >
                      {weekDiff >= 0 ? "▲" : "▼"} {Math.abs(weekDiffPct)}%
                    </span>
                  )}
                </div>
              ) : (
                <span
                  style={{
                    fontSize: "12px",
                    color: "#94a3b8",
                    marginLeft: "auto",
                  }}
                >
                  —
                </span>
              )}
            </div>
          </div>
        )}

        {/* ── Box 2: Stats grid — reduced width/space ── */}
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "1fr",
            gap: "4px",
            background: "#f8fafc",
            border: "1px solid #e2e8f0",
            borderRadius: "12px",
            padding: "8px 12px",
            minWidth: "0",
            width: "100%",
            boxSizing: "border-box",
            height: "100px",
            position: "relative",
          }}
        >
          {/* Formula tooltip icon */}
          <div
            style={{ position: "absolute", top: "8px", right: "10px" }}
            onMouseEnter={() => setShowFormula(true)}
            onMouseLeave={() => setShowFormula(false)}
          >
            <FaInfoCircle
              style={{ fontSize: "13px", color: "#94a3b8", cursor: "pointer" }}
            />
            {showFormula && (
              <div
                style={{
                  position: "absolute",
                  top: "20px",
                  right: 0,
                  background: "#1e293b",
                  color: "#f1f5f9",
                  borderRadius: "10px",
                  padding: "12px 16px",
                  fontSize: "12px",
                  lineHeight: "1.8",
                  zIndex: 999,
                  whiteSpace: "nowrap",
                  boxShadow: "0 8px 24px rgba(0,0,0,0.25)",
                  pointerEvents: "none",
                }}
              >
                <div
                  style={{
                    fontWeight: 700,
                    color: "#7dd3fc",
                    marginBottom: "6px",
                    fontSize: "11px",
                    textTransform: "uppercase",
                    letterSpacing: "0.6px",
                  }}
                >
                  % Calculation
                </div>
                <div>
                  <span style={{ color: "#fde68a" }}>%</span> = (Weekly Avg ÷
                  Target) × 100
                </div>
                <div
                  style={{
                    borderTop: "1px solid #334155",
                    marginTop: "6px",
                    paddingTop: "6px",
                  }}
                >
                  <div>
                    <span style={{ color: "#86efac" }}>Weekly Avg</span> = Total
                    Revenue ÷ No. of Weeks
                  </div>
                  <div>
                    <span style={{ color: "#86efac" }}>Target</span> = £
                    {maxValue.toLocaleString("en-GB")}
                  </div>
                </div>
              </div>
            )}
          </div>
          {/* Row 1 — Current Year (paddingRight clears the info icon top-right) */}
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "6px",
              paddingRight: "18px",
            }}
          >
            <FaDotCircle
              style={{ color: "#4361ee", fontSize: "10px", flexShrink: 0 }}
            />
            <span
              style={{
                fontSize: "12px",
                color: "#374151",
                flexShrink: 0,
                whiteSpace: "nowrap",
              }}
            >
              Current Year ({currentYear})
            </span>
            <span
              style={{
                fontSize: "13px",
                fontWeight: 700,
                color: "#1e40af",
                marginLeft: "auto",
              }}
            >
              {fmtGBP(currentTotal)}
            </span>
          </div>
          {/* Row 2 — Previous Year (same paddingRight as Row 1 so values align) */}
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "6px",
              paddingRight: "18px",
              borderTop: "1px dashed #e2e8f0",
              paddingTop: "4px",
            }}
          >
            <FaDotCircle
              style={{ color: "#4CBB17", fontSize: "10px", flexShrink: 0 }}
            />
            <span
              style={{
                fontSize: "12px",
                color: "#374151",
                flexShrink: 0,
                whiteSpace: "nowrap",
              }}
            >
              Previous Year ({previousYear})
            </span>
            <span
              style={{
                fontSize: "13px",
                fontWeight: 700,
                color: "#166534",
                marginLeft: "auto",
              }}
            >
              {fmtGBP(previousTotal)}
            </span>
          </div>
          {/* Row 3 — Difference (same paddingRight as Row 1/2 so values align) */}
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "6px",
              paddingRight: "18px",
              borderTop: "1px dashed #e2e8f0",
              paddingTop: "4px",
            }}
          >
            <span style={{ fontSize: "12px", color: "#374151", flexShrink: 0 }}>
              Difference
            </span>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                flexWrap: "wrap",
                justifyContent: "flex-end",
                gap: "4px 6px",
                marginLeft: "auto",
                minWidth: 0,
              }}
            >
              <span
                style={{
                  fontSize: "13px",
                  fontWeight: 700,
                  color: diff >= 0 ? "#15803d" : "#be123c",
                }}
              >
                {diff >= 0 ? "+" : "-"}
                {fmtGBP(diff)}
              </span>
              {diffPct !== null && (
                <span
                  style={{
                    fontSize: "13px",
                    fontWeight: 700,
                    color: diff >= 0 ? "#15803d" : "#be123c",
                  }}
                >
                  {diff >= 0 ? "▲" : "▼"} {Math.abs(diffPct)}%
                </span>
              )}
            </div>
          </div>
        </div>

        {/* ── Box 3: Week-to-Week — reduced width/space to match Tabs 1/2 ── */}
        {showWeekCard && (
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "1fr",
              gap: "4px",
              background: "#f8fafc",
              border: "1px solid #e2e8f0",
              borderRadius: "12px",
              padding: "8px 12px",
              minWidth: "0",
              width: "100%",
              boxSizing: "border-box",
              minHeight: "110px",
            }}
          >
            <div
              style={{
                fontSize: "10px",
                fontWeight: 700,
                color: "#64748b",
                textTransform: "uppercase",
                letterSpacing: "0.6px",
                marginBottom: "2px",
                lineHeight: "1.2",
              }}
            >
              Week-to-Week Year-on-Year Comparison
            </div>

            {/* Current Year — single line to match Card 1/2 height: label + [Enter week][Enter value] */}
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: "6px",
                minWidth: 0,
              }}
            >
              <FaDotCircle
                style={{ color: "#4361ee", fontSize: "10px", flexShrink: 0 }}
              />
              <span
                style={{
                  fontSize: "12px",
                  color: "#374151",
                  flexShrink: 1,
                  minWidth: "148px",
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                }}
              >
                Current Year ({currentYear})
              </span>
              <div
                style={{
                  display: "flex",
                  gap: "6px",
                  marginLeft: "auto",
                  minWidth: 0,
                  flexShrink: 1,
                  justifyContent: "flex-end",
                }}
              >
                <input
                  type="number"
                  min={1}
                  max={53}
                  value={wtwCurrentWeek}
                  onChange={handleWtwCurrentWeekChange}
                  placeholder="Enter week"
                  style={{
                    flex: "0 1 60px",
                    minWidth: "52px",
                    maxWidth: "70px",
                    boxSizing: "border-box",
                    fontSize: "12px",
                    border: wtwWeekErrorCurrent
                      ? "1px solid #fca5a5"
                      : "1px solid #cbd5e1",
                    borderRadius: "6px",
                    padding: "2px 6px",
                    outline: "none",
                    background: "#fff",
                  }}
                />
                <input
                  type="number"
                  value={wtwCurrentInput}
                  onChange={handleWtwCurrentChange}
                  placeholder="Enter value"
                  style={{
                    flex: "1 1 auto",
                    minWidth: "40px",
                    maxWidth: "100px",
                    boxSizing: "border-box",
                    fontSize: "12px",
                    border: "1px solid #cbd5e1",
                    borderRadius: "6px",
                    padding: "2px 6px",
                    outline: "none",
                    background: "#fff",
                  }}
                />
              </div>
            </div>
            {wtwWeekErrorCurrent && (
              <span
                style={{
                  fontSize: "10px",
                  color: "#dc2626",
                  fontWeight: 600,
                  marginTop: "-2px",
                }}
              >
                {wtwWeekErrorCurrent}
              </span>
            )}

            {/* Previous Year — single line */}
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: "6px",
                minWidth: 0,
                borderTop: "1px dashed #e2e8f0",
                paddingTop: "4px",
              }}
            >
              <FaDotCircle
                style={{ color: "#4CBB17", fontSize: "10px", flexShrink: 0 }}
              />
              <span
                style={{
                  fontSize: "12px",
                  color: "#374151",
                  flexShrink: 1,
                  minWidth: "148px",
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                }}
              >
                Previous Year ({wtwPreviousYear})
              </span>
              <div
                style={{
                  display: "flex",
                  gap: "6px",
                  marginLeft: "auto",
                  minWidth: 0,
                  flexShrink: 1,
                  justifyContent: "flex-end",
                }}
              >
                <input
                  type="number"
                  min={1}
                  max={53}
                  value={wtwPrevWeek}
                  onChange={handleWtwPrevWeekChange}
                  placeholder="Enter week"
                  style={{
                    flex: "0 1 60px",
                    minWidth: "52px",
                    maxWidth: "70px",
                    boxSizing: "border-box",
                    fontSize: "12px",
                    border: wtwWeekErrorPrev
                      ? "1px solid #fca5a5"
                      : "1px solid #cbd5e1",
                    borderRadius: "6px",
                    padding: "2px 6px",
                    outline: "none",
                    background: "#fff",
                  }}
                />
                <input
                  type="number"
                  value={wtwPrevInput}
                  onChange={handleWtwPrevChange}
                  placeholder="Enter value"
                  style={{
                    flex: "1 1 auto",
                    minWidth: "40px",
                    maxWidth: "100px",
                    boxSizing: "border-box",
                    fontSize: "12px",
                    border: "1px solid #cbd5e1",
                    borderRadius: "6px",
                    padding: "2px 6px",
                    outline: "none",
                    background: "#fff",
                  }}
                />
              </div>
            </div>
            {wtwWeekErrorPrev && (
              <span
                style={{
                  fontSize: "10px",
                  color: "#dc2626",
                  fontWeight: 600,
                  marginTop: "-2px",
                }}
              >
                {wtwWeekErrorPrev}
              </span>
            )}

            {/* Difference — READ-ONLY, auto calculated from Current/Previous */}
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: "6px",
                borderTop: "1px dashed #e2e8f0",
                paddingTop: "4px",
              }}
            >
              <span
                style={{
                  fontSize: "12px",
                  color: "#374151",
                  flexShrink: 0,
                }}
              >
                Difference
              </span>
              {wtwDiff !== null && wtwDiffPctDisplay !== null ? (
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    flexWrap: "wrap",
                    justifyContent: "flex-end",
                    gap: "4px 6px",
                    marginLeft: "auto",
                    minWidth: 0,
                  }}
                >
                  <span
                    style={{
                      fontSize: "13px",
                      fontWeight: 700,
                      color: wtwDiff >= 0 ? "#15803d" : "#be123c",
                    }}
                  >
                    {wtwDiff >= 0 ? "+" : "-"}
                    {fmtGBP(wtwDiff)}
                  </span>
                  <span
                    style={{
                      fontSize: "13px",
                      fontWeight: 700,
                      color: wtwDiff >= 0 ? "#15803d" : "#be123c",
                    }}
                  >
                    {wtwDiff >= 0 ? "▲" : "▼"}{" "}
                    {Math.abs(Number(wtwDiffPctDisplay))}%
                  </span>
                </div>
              ) : wtwDiff !== null ? (
                <span
                  style={{
                    fontSize: "13px",
                    fontWeight: 700,
                    color: wtwDiff >= 0 ? "#15803d" : "#be123c",
                    marginLeft: "auto",
                  }}
                >
                  {wtwDiff >= 0 ? "+" : "-"}
                  {fmtGBP(wtwDiff)}
                </span>
              ) : (
                <span
                  style={{
                    fontSize: "12px",
                    color: "#94a3b8",
                    marginLeft: "auto",
                  }}
                >
                  —
                </span>
              )}
            </div>
            {/* Graceful handling for missing weeks — independent */}
            {((wtwCurrentWeek &&
              wtwCurrentInput === "" &&
              !wtwWeekErrorCurrent) ||
              (wtwPrevWeek && wtwPrevInput === "" && !wtwWeekErrorPrev)) && (
              <div
                style={{
                  fontSize: "10px",
                  color: "#92400e",
                  background: "#fefce8",
                  border: "1px solid #fde68a",
                  borderRadius: "6px",
                  padding: "4px 8px",
                  marginTop: "2px",
                }}
              >
                {wtwCurrentWeek &&
                wtwCurrentInput === "" &&
                wtwPrevWeek &&
                wtwPrevInput === "" &&
                wtwCurrentWeek === wtwPrevWeek
                  ? `No data for Week ${wtwCurrentWeek} in either year`
                  : wtwCurrentWeek &&
                    wtwCurrentInput === "" &&
                    !wtwWeekErrorCurrent &&
                    wtwPrevWeek &&
                    wtwPrevInput === "" &&
                    !wtwWeekErrorPrev &&
                    wtwCurrentWeek !== wtwPrevWeek
                  ? `No data for Week ${wtwCurrentWeek}, ${currentYear} & Week ${wtwPrevWeek}, ${wtwPreviousYear}`
                  : wtwCurrentWeek &&
                    wtwCurrentInput === "" &&
                    !wtwWeekErrorCurrent
                  ? `No data for Week ${wtwCurrentWeek}, ${currentYear}`
                  : `No data for Week ${wtwPrevWeek}, ${wtwPreviousYear} — showing Current only`}
              </div>
            )}
            {hasWtwPrev && wtwPrevNum === 0 && wtwDiff !== null && (
              <div
                style={{
                  fontSize: "10px",
                  color: "#92400e",
                  background: "#fefce8",
                  border: "1px solid #fde68a",
                  borderRadius: "6px",
                  padding: "4px 8px",
                }}
              >
                Previous Year is 0 — % shows 0% or 100% (division avoided)
              </div>
            )}
          </div>
        )}
      </div>

      <Chart options={options} series={series} type="area" height={320} />

      {/* Legend */}
      <div
        className="d-flex justify-content-center gap-4 mt-2"
        style={{ borderTop: "1px solid #f0f0f0", paddingTop: "12px" }}
      >
        {[
          {
            color: "#4361ee",
            label: `Current Year (${currentYear})`,
            type: "line",
          },
          {
            color: "#4CBB17",
            label: `Previous Year (${previousYear})`,
            type: "dash",
          },
        ].map((l, i) => (
          <div key={i} className="d-flex align-items-center gap-1">
            {l.type === "line" ? (
              <span
                style={{
                  width: 16,
                  height: 2,
                  background: l.color,
                  display: "inline-block",
                }}
              />
            ) : (
              <span
                style={{
                  width: 16,
                  height: 2,
                  borderTop: `2px dashed ${l.color}`,
                  display: "inline-block",
                }}
              />
            )}
            <small
              style={{ color: "#1a1a2e", fontWeight: 600, fontSize: "11px" }}
            >
              {l.label}
            </small>
          </div>
        ))}
      </div>
    </div>
  )
}

export default SuccessRateChart
